import { type ReactNode, useMemo, useState } from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';

export interface TableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Funkcja dostępu do wartości komórki. Jeśli zwróci primitive — to też jest sort value. */
  accessor: (row: Row) => ReactNode;
  /** Opcjonalna funkcja zwracająca wartość do sortowania (gdy accessor zwraca ReactNode z formatowaniem). */
  sortValue?: (row: Row) => string | number | boolean | null | undefined;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

export interface TableProps<Row> {
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  pageSize?: number;
  emptyState?: ReactNode;
  caption?: string;
  onRowClick?: (row: Row) => void;
  /** Dodatkowa klasa per wiersz (np. podświetlenie aktywnego/rozwiniętego). */
  rowClassName?: (row: Row) => string;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export function Table<Row>({
  columns,
  rows,
  rowKey,
  pageSize = 20,
  emptyState,
  caption,
  onRowClick,
  rowClassName,
}: TableProps<Row>) {
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const valueOf = col.sortValue ?? ((row: Row) => col.accessor(row) as unknown);
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paged = sorted.slice(start, start + pageSize);

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
    setPage(1);
  };

  if (rows.length === 0 && emptyState) {
    return (
      <div className="rounded-xl border border-border-secondary bg-bg-elevated p-8 text-center text-sm text-text-muted">
        {emptyState}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-xl border border-border-secondary bg-bg-elevated">
        <table className="w-full text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="bg-bg-secondary text-text-secondary">
            <tr>
              {columns.map((col) => {
                const isSorted = sort?.key === col.key;
                const ariaSort = isSorted
                  ? sort!.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={col.sortable ? ariaSort : undefined}
                    className={['px-4 py-3 text-left font-medium', col.headerClassName ?? ''].join(
                      ' ',
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className="inline-flex items-center gap-1 hover:text-text-primary transition-colors"
                      >
                        {col.header}
                        {isSorted ? (
                          sort!.dir === 'asc' ? (
                            <ChevronUp size={14} aria-hidden="true" />
                          ) : (
                            <ChevronDown size={14} aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown size={14} aria-hidden="true" className="opacity-50" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={[
                  'border-t border-border-secondary text-text-primary',
                  onRowClick ? 'cursor-pointer hover:bg-bg-secondary transition-colors' : '',
                  rowClassName?.(row) ?? '',
                ].join(' ')}
              >
                {columns.map((col) => (
                  <td key={col.key} className={['px-4 py-3', col.className ?? ''].join(' ')}>
                    {col.accessor(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Strona {safePage} z {totalPages} ({sorted.length} wierszy)
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="rounded-lg px-3 py-1 hover:bg-bg-tertiary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Poprzednia
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="rounded-lg px-3 py-1 hover:bg-bg-tertiary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Następna
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
